import React, { createContext, memo, useContext, useEffect, useMemo, useRef } from "react"
import _ from "lodash"

interface IApiConf {
  apiEndpoint: string,
  locale: string,
  authToken?: string,
}

interface PromisePayload<Res> {
  data?: Res
  error?: Base.Api.Error
}

interface IApiSender {
  send<Req, Res>(action: string, data?: Req): Promise<{data?: Res, error?: Base.Api.Error}>
}

export type IApi = {configure(conf: IApiConf): void} & IApiSender

const ApiContext = createContext<IApiSender>({
  send: () => { return new Promise((resolve) => {
    resolve({ error: { error: "dummy"} })
  }) }
})
ApiContext.displayName = "ApiContext"

export const createApi = (): IApi => {
  let apiEndpoint = ""
  let locale = "de"
  let configured = false
  let authToken: string | undefined
  return {
    configure(conf: IApiConf) {
      configured = true
      apiEndpoint = conf.apiEndpoint
      locale = conf.locale
      authToken = conf.authToken
    },
    send<Req, Resp>(action: string, data?: Req) {
      if (!configured) {
        throw new Error("Call api.configure(...) before usage!")
      }
      return rawSend<Resp>(apiEndpoint, locale, authToken, action, data)
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function rawSend<Resp> (apiEndpoint: string, locale: string, authToken: string | undefined, action: string, data?: any): Promise<PromisePayload<Resp>> {
  const settings = {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json;charset=utf-8",
      "X-Locale": locale,
    }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any
  if (!data) { data = {} }
  if (authToken) {
    settings.headers["X-CSRF-Token"] = authToken
    data = {...data, authenticity_token: authToken}
  }

  settings.body = JSON.stringify(data)

  return fetch(apiEndpoint + "/" + action, settings)
    .then((resp) => resp.json())
    .then((jsonResp) => {
      if (_.isString(jsonResp.error)) {
        return { error: jsonResp }
      }
      return { data: jsonResp }
    })
}

export const useApi = (): IApiSender => {
  return useContext(ApiContext)
}

const WithApiInner: React.FC<React.PropsWithChildren<IApiConf>> = ({ apiEndpoint, locale, children, authToken }) => {
  const initialSettingsRef = useRef({
    apiEndpoint,
    locale,
    authToken,    
  })

  const api = useMemo<{api: ReturnType<typeof createApi>, context: IApiSender}>(() => {
    const api = createApi()
    api.configure(initialSettingsRef.current)
    return {
      api,
      context: { send: api.send }
    }
  }, [])

  const apiRef = useRef(api)
  apiRef.current = api

  useEffect(() => { // update api setup when changed
    apiRef.current.api.configure({apiEndpoint, locale, authToken})
  }, [apiEndpoint, locale, authToken])

  return <ApiContext.Provider value={api.context}>{children}</ApiContext.Provider>
}
export const WithApi = memo(WithApiInner)
